import { useState } from 'react';
import { MS_PER_DAY } from '../../lib/format.js';
import { getThemeVars } from '../../config/theme.js';
import { db } from '../../lib/supabase.js';
import type { Profile, TranslateFn } from '../../types';

interface PendingDeletionScreenProps {
  user: Profile | null | undefined;
  darkMode: boolean;
  onLogout: () => void;
  t: TranslateFn;
}

/**
 * PendingDeletionScreen — Karenz-Screen für soft-gelöschte Konten.
 *
 * Zeigt die verbleibenden Tage bis zur endgültigen Löschung (30-Tage-Frist)
 * und bietet Wiederherstellung via `db.restoreAccount()`. Nach erfolgreicher
 * Wiederherstellung wird die Seite neu geladen, damit der frische
 * Auth-State übernommen wird.
 */
export function PendingDeletionScreen({
  user,
  darkMode,
  onLogout,
  t,
}: PendingDeletionScreenProps) {
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  let verbleibend: number | null = null;
  if (user && user.geloeschtAm) {
    const elapsedMs = Date.now() - new Date(user.geloeschtAm).getTime();
    const elapsedTage = Math.floor(elapsedMs / MS_PER_DAY);
    verbleibend = Math.max(0, 30 - elapsedTage);
  }

  async function handleRestore(): Promise<void> {
    setBusy(true);
    setError(null);
    const res = await db.restoreAccount();
    if (res.error) {
      setError(t('kontoWiederherstellenFehler') + ': ' + String(res.error));
      setBusy(false);
      return;
    }
    window.location.reload();
  }

  return (
    <div
      style={{
        fontFamily: "'DM Sans',sans-serif",
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center' as const,
        ...getThemeVars(darkMode),
      }}>
      <div style={{ maxWidth: 480, width: '100%' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>
          {'\u26A0\uFE0F'}
        </div>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 800,
            marginBottom: 8,
            color: 'var(--danger)',
          }}>
          {t('kontoZurLoeschungMarkiert')}
        </h2>
        {user && user.email &&
          <p
            style={{
              fontSize: 13,
              color: 'var(--muted)',
              marginBottom: 4,
            }}>
            {user.email}
          </p>}
        {verbleibend !== null &&
          <p
            style={{
              fontSize: 14,
              color: 'var(--text)',
              lineHeight: 1.6,
              marginTop: 16,
              marginBottom: 24,
            }}>
            {t('kontoLoeschungVerbleibend')}
            {' '}
            <strong style={{ color: 'var(--danger)', fontSize: 18 }}>
              {verbleibend}
            </strong>
            {' '}
            {t('kontoLoeschungTage')}
          </p>}
        {error &&
          <div
            style={{
              fontSize: 12,
              color: '#ef4444',
              background: 'rgba(239,68,68,.08)',
              border: '1px solid rgba(239,68,68,.2)',
              borderRadius: 8,
              padding: '8px 12px',
              marginBottom: 16,
            }}>
            {error}
          </div>}
        <button
          onClick={() => void handleRestore()}
          disabled={busy}
          style={{
            display: 'block',
            width: '100%',
            padding: '12px 20px',
            fontSize: 14,
            fontWeight: 700,
            color: '#000',
            background: busy ? 'var(--surface2)' : 'var(--accent)',
            border: 'none',
            borderRadius: 10,
            cursor: busy ? 'not-allowed' : 'pointer',
            marginBottom: 10,
            fontFamily: "'DM Sans',sans-serif",
          }}>
          {busy ? '\u2026' : t('kontoWiederherstellen')}
        </button>
        <button
          onClick={onLogout}
          disabled={busy}
          style={{
            display: 'block',
            width: '100%',
            padding: '10px 20px',
            fontSize: 13,
            color: '#ef4444',
            background: 'transparent',
            border: '1px solid #ef4444',
            borderRadius: 10,
            cursor: busy ? 'not-allowed' : 'pointer',
            fontFamily: "'DM Sans',sans-serif",
          }}>
          {t('abmelden')}
        </button>
      </div>
    </div>
  );
}
